<template>
  <div class="input--wrap date--wrap"
       :class="[`label-${labelPosition}`, {'above-input': isInputAbove}]"
       :id="`calendar${id}`" >
    <label :for="id" class="label"><slot /></label>
    <div class="input-element"
         :class="{ error: modelError || error }">
      <span @click="onClick">
        <input type="text"
               readonly="readonly"
               :disabled="disabled"
               :class="{ disabled: disabled }"
               :autocomplete="autocomplete"
               :id="id"
               :placeholder="placeholder"
               :name="name"
               :key="updateKey"
               v-model="model" />
      </span>
      <i class="icon icon--calendar-input"></i>
      <i v-if="reset && !disabled && model.length"
         class="icon icon--close14"
         @click="resetValue"></i>
      <transition name="fade">
        <strong v-if="modelError.length" class="validation-text">{{ $t(`common.validationErrorText.${modelError}`) }}</strong>
        <strong v-if="error.length" class="validation-text">{{ error }}</strong>
        <strong v-else-if="modelDescription.length" class="validation-text description">{{ modelDescription }}</strong>
      </transition>
    </div>

<!--    <transition name="ui-selectbox-slide">-->
      <div class="calendar"
           v-show="showCalendar"
           @mouseenter="resetDropdownTimer"
           @mouseleave="startDropdownTimer"
           @scroll.stop>
        <div class="timezone">
          <span>({{timezone}}) {{timezoneName}}</span>
        </div>
        <month-calendar :monthStr="monthString"
                        :targetDate="new Date(model)"
                        :minDate="minDate"
                        :maxDate="maxDate"
                        @onMonthAdd="onMonthAdd"
                        @onDateClick="onInputDate"/>
      </div>
<!--    </transition>-->
  </div>
</template>

<script>
import { addMonths, format, parse } from 'date-fns'

import MonthCalendar from '@/components/ui/ba/MonthCalendar'
import { getOffset } from '@/libs/utils'

export default {
  name: 'UiInputDate',
  components: {
    MonthCalendar
  },
  props: {
    isInputAbove: {
      type: Boolean,
      default: true,
      required: false
    },
    autocomplete: {
      type: String,
      default: 'on',
      required: false
    },
    labelPosition: {
      type: String, // top, left
      default: 'top',
      required: false
    },
    id: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      default: 'Select a date',
      required: false
    },
    name: {
      type: String,
      default: 'inputValue',
      required: false
    },
    modelValue: {
      default: '',
      required: false
    },
    modelError: {
      type: String,
      default: '',
      required: false
    },
    error: {
      type: String,
      default: '',
      required: false
    },
    modelDescription: {
      type: String,
      default: '',
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    reset: {
      type: Boolean,
      default: true,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    monthStr: {
      type: Boolean,
      default: false,
      required: false
    },
    timezoneName: {
      type: String,
      default: '',
      required: false
    }
  },
  watch: {
    modelValue (value) {
      this.model = value
    },
    focused (value) {
      if (!value) {
        this.updateKey++
      }
    },
    reset (value) {
      if (value) {
        this.model = ''
      }
    },
    showCalendar (value) {
      this.monthString = format(this.model ? parse(this.model) : new Date(), 'YYYY-MM-DD')

      let bodyListener = (e) => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === `calendar${this.id}`) {
              outside = false
            }
          }
        }
        if (outside) {
          this.showCalendar = false
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    }
  },
  created () {
    if (this.modelValue) {
      this.model = this.modelValue
    }

    if (!this.monthStr) {
      this.monthString = format(this.model ? parse(this.model) : new Date(), 'YYYY-MM-DD')
    }
  },
  data () {
    return {
      model: '',
      updateKey: 0,
      monthString: null,
      showCalendar: false,
      dropdownTimer: null,
      dropdownTimerTime: null
    }
  },
  computed: {
    timezone () {
      return getOffset(this.timezoneName)
    }
  },
  methods: {
    resetValue () {
      this.model = ''
      this.$emit('input', this.model)
    },
    onMonthAdd (months) {
      let monthDate = parse(this.monthString)
      monthDate = addMonths(monthDate, months)
      this.monthString = format(monthDate, 'YYYY-MM-DD')
    },
    onInputDate (value) {
      this.onClick()
      this.$emit('input', format(value, 'YYYY-MM-DD'))
    },
    onClick () {
      if (!this.disabled) {
        this.showCalendar = !this.showCalendar
        if (!this.showCalendar) {
          this.resetDropdownTimer()
        }
        this.$emit('onFocus')
      }
    },
    startDropdownTimer: function () {
      this.dropdownTimer = setInterval(() => {
        if (this.dropdownTimerTime < 0) {
          this.showCalendar = false
          this.resetDropdownTimer()
        }
        this.dropdownTimerTime--
      }, 1000)
    },
    resetDropdownTimer: function () {
      this.dropdownTimerTime = 3
      clearInterval(this.dropdownTimer)
      this.dropdownTimer = null
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.input--wrap {
  width: 100%;
  position: relative;
  display: inline-block;
  text-align: left;
  margin: 0 auto;

  &.label-left {
    @include flexYcenter;
    justify-content: center;
    .label {
      display: inline-block;
      margin-right: 10px;
      margin-bottom: 0;
    }
    input {
      display: inline-block;
    }

    &.above-input {
      margin-top: 40px;
    }
  }

  &.above-input {
    margin-top: 22px;
  }

  .label {
    display: inline-block;
    font-weight: 500;
    padding-left: 5px;
    margin-bottom: 5px;
    font-size: 13px;
    color: #767676;
  }

  .input-element {
    position: relative;
    width: 100%;

    &.error {
      input {
        border-color: $errorColor;
      }
      .validation-text {
        color: $errorColor;

        .description {
          color: #289eef;
        }
      }
    }

    input {
      display: block;
      width: 100%;
      height: 38px;
      line-height: 36px;
      border-radius: 6px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      font-size: 14px;
      padding-left: 34px;
      padding-right: 40px;
      box-sizing: border-box;
      transition: opacity 0.2s ease;
      transform: translateZ(0);
      color: #2f2f2f;

      &::-ms-clear {
        display: none;
      }
      &::-ms-reveal {
        display: none;
      }

      &:focus {
        border-color: #289eef;
      }

      &[disabled] {
        background-color: #d7d7d7;
        opacity: 0.3;

        &::placeholder {
          color: #999999;
        }
      }
    }
  }

  .icon--calendar-input {
    position: absolute;
    top: calc(50% - 10px);
    left: 9px;
  }

  .icon--close14 {
    position: absolute;
    top: calc(50% - 16px);
    right: 2px;
    padding: 9px 12px;
    cursor: pointer;
    background-position: center;
    background-color: transparent;
  }

  .validation-text {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    padding-left: 5px;
    font-weight: 500;

    &.description {
      color: #289eef;
    }

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.15s ease;
    }
    &.fade-enter,
    &.fade-leave-to{
      opacity: 0;
    }
  }

  .calendar {
    width: 260px;
    position: absolute;
    top: 40px;
    left: -5px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    border-radius: 14px;
    z-index: 10;
    padding: 22px 20px 30px 20px;
    background-color: #fff;
    transition: transform 0.2s ease, opacity 0.1s ease;
    transform: scaleY(1);
    opacity: 1;
    transform-origin: top;

    & .timezone {
      margin-bottom: 23px;
    }

    &.ui-selectbox-slide-enter,
    &.ui-selectbox-slide-leave-to{
      transform: scaleY(0);
      opacity: 0;
    }

    & /deep/ {
      .month_calendar {
        border-radius: 15px;

        .month_control {
          padding-bottom: 25px;
        }
      }

      td {
        transition: background-color 0.2s ease,
        color 0.2s ease;

        &.target {
          background-color: #ec0147;
          color: #fff;
        }
      }
    }
  }
}
</style>
